/**
 * Customer Data Requests
 * - Swaps between the request form and the cookie-guidance panel based on
 *   the selected jurisdiction.
 * - Fills the response-window note for the selected jurisdiction.
 * - Requires at least one request type before submit.
 * - Supports deep links: /pages/customer-data-requests?jurisdiction=California
 *   (matches the option value, case-insensitive; also matches the 2-letter code).
 */
class CustomerDataRequests extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.clearRequestsError = this.clearRequestsError.bind(this);
  }

  connectedCallback() {
    this.select = this.querySelector('[data-cdr-select]');
    this.form = this.querySelector('form');
    this.formPanel = this.querySelector('[data-cdr-panel="form"]');
    this.otherPanel = this.querySelector('[data-cdr-panel="other"]');
    this.note = this.querySelector('[data-cdr-window-note]');
    this.requestsWrap = this.querySelector('[data-cdr-requests]');
    this.requestsError = this.querySelector('[data-cdr-requests-error]');

    if (!this.select) return; // success state - nothing to wire up

    this.select.addEventListener('change', this.handleChange);

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }

    if (this.requestsWrap) {
      this.requestsWrap.addEventListener('change', this.clearRequestsError);
    }

    this.applyDeepLink();
    this.render();
  }

  disconnectedCallback() {
    if (this.select) this.select.removeEventListener('change', this.handleChange);
    if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
    if (this.requestsWrap) this.requestsWrap.removeEventListener('change', this.clearRequestsError);
  }

  /* ---------- state ---------- */

  get selectedOption() {
    const opt = this.select.options[this.select.selectedIndex];
    return opt && opt.value !== '' ? opt : null;
  }

  applyDeepLink() {
    if (this.select.value !== '') return; // posted value wins

    let wanted = '';
    try {
      wanted = new URLSearchParams(window.location.search).get('jurisdiction') || '';
    } catch (e) {
      return;
    }
    if (!wanted) return;

    const needle = wanted.trim().toLowerCase();
    const match = Array.from(this.select.options).find((opt) => {
      if (opt.value === '') return false;
      return (
        opt.value.toLowerCase() === needle ||
        (opt.dataset.code || '').toLowerCase() === needle
      );
    });

    if (match) this.select.value = match.value;
  }

  /* ---------- rendering ---------- */

  render() {
    const opt = this.selectedOption;
    const group = opt ? opt.dataset.group : null;

    this.togglePanel(this.formPanel, group === 'us' || group === 'intl');
    this.togglePanel(this.otherPanel, group === 'other');
    this.updateNote(opt);
  }

  togglePanel(panel, show) {
    if (!panel) return;
    panel.hidden = !show;

    // Only fields in the visible panel should participate in native validation.
    panel.querySelectorAll('input, select, textarea, button').forEach((el) => {
      el.disabled = !show;
    });
  }

  updateNote(opt) {
    if (!this.note) return;
    const template = this.note.dataset.template || '';
    const window = opt ? opt.dataset.window || '' : '';

    if (!opt || !window) {
      this.note.hidden = true;
      this.note.textContent = '';
      return;
    }

    this.note.textContent = template
      .replace(/\[jurisdiction\]/g, opt.textContent.trim())
      .replace(/\[window\]/g, window);
    this.note.hidden = false;
  }

  /* ---------- events ---------- */

  handleChange() {
    this.render();
  }

  handleSubmit(event) {
    if (!this.formPanel || this.formPanel.hidden) {
      // Guidance panel has no submit button; block just in case.
      event.preventDefault();
      return;
    }

    if (this.requestsWrap) {
      const anyChecked = this.requestsWrap.querySelector('input[type="checkbox"]:checked');
      if (!anyChecked) {
        event.preventDefault();
        if (this.requestsError) this.requestsError.hidden = false;
        const first = this.requestsWrap.querySelector('input[type="checkbox"]');
        if (first) first.focus();
      }
    }
  }

  clearRequestsError() {
    if (this.requestsError) this.requestsError.hidden = true;
  }
}

if (!customElements.get('customer-data-requests')) {
  customElements.define('customer-data-requests', CustomerDataRequests);
}
